import { Stack, Typography, Skeleton } from '@mui/material';
import {
  AccountState,
  ResourceType,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { TimeSelectorPopover } from '@/components/date/timeSelectorPopover';
import { SnapshotIndicator } from '@/components/snapshots/SnapshotIndicator';
import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { TIME_FORMAT, type Dayjs } from '@/utils/dayjs';

import { Panels } from '../../../panels';

export const ContinuousSnapshotRow = ({
  resource,
  selectedDate,
  dateRange,
  timeType,
  vault,
}: {
  resource: InventoryResource;
  selectedDate: Dayjs;
  dateRange: { start: Dayjs; end: Dayjs };
  timeType: 'latest-available' | 'specific-time';
  vault?: BackupVault;
}) => {
  const isLastDayInRange = dateRange.end.isSame(selectedDate, 'day');
  const { isAuthorizedResource } = useRoles();
  const { rightPanel } = useWorkspace();
  const dal = useDAL();
  const { data: restoreAccounts } = dal.cloudAccounts.restore.useList();
  const isAuthorized = isAuthorizedResource(
    'create:restore_resource',
    resource
  );
  const startTime = dateRange.start.isBefore(selectedDate.startOf('day'))
    ? selectedDate.startOf('day')
    : dateRange.start;
  const endTime = dateRange.end.isAfter(selectedDate.endOf('day'))
    ? selectedDate.endOf('day')
    : dateRange.end;
  const [time, setTime] = useState(endTime);
  useEffect(() => {
    setTime(endTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);
  const { sourceAccounts } = useSourceAccounts();
  const account = sourceAccounts?.find(
    (account) => account.providerAccountId === resource.providerAccountId
  );

  const onClose = () => {
    rightPanel.setIsOpen(false);
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  const restoreToTime =
    timeType === 'latest-available'
      ? isLastDayInRange
        ? undefined
        : selectedDate.endOf('day').toDate()
      : time.toDate();

  const onRestore = () => {
    if (resource.resourceType === ResourceType.GcpBigQuery) {
      rightPanel.setComponent({
        panel: Panels.RestoreBigQueryWizard,
        props: {
          resource,
          restoreToTime,
          onClose,
          onBack,
        },
      });
    } else if (resource.resourceType === ResourceType.AwsRds) {
      rightPanel.setComponent({
        panel: Panels.RestoreRDSPitrWizard,
        props: {
          resource,
          restoreToTime,
          onClose,
          onBack,
        },
      });
    } else if (resource.resourceType === ResourceType.AzureSqlVirtualMachine) {
      rightPanel.setComponent({
        panel: Panels.RestoreAzureVMPitrWizard,
        props: {
          resource,
          restoreToTime,
          vault,
          onClose,
          onBack,
        },
      });
    }
  };

  const sourceAccountAlsoRestore = restoreAccounts?.accounts.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap='100px'
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='12px'
        >
          <SnapshotIndicator
            backgroundColor={vault?.backgroundColor || null}
            isNative={!vault}
          />
          {timeType === 'specific-time' ? (
            <TimeSelectorPopover
              minTime={startTime}
              maxTime={endTime}
              hours={time.hour()}
              minutes={time.minute()}
              seconds={time.second()}
              showSeconds={true}
              showUTC={true}
              onChange={(hours, minutes, seconds) =>
                setTime(
                  selectedDate
                    .hour(hours)
                    .minute(minutes)
                    .second(seconds ?? 0)
                )
              }
            />
          ) : (
            <Typography>{`${endTime.format(TIME_FORMAT)} UTC`}</Typography>
          )}
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='12px'
        >
          <Typography variant='body1'>Stored in</Typography>
          {vault?.id ? (
            <VaultTag vault={vault} />
          ) : account ? (
            <AccountAsVaultTag
              cloudProvider={account.sourceAccountAttributes.cloudProvider}
              region={resource.region}
            />
          ) : (
            <Skeleton variant='text' width={100} height={24} />
          )}
        </Stack>
      </Stack>
      <AuthorizationTooltip
        overrideTitle
        title={
          vault || sourceAccountAlsoRestore
            ? ''
            : 'Restore requires the source account to be connected as a restore account.'
        }
        isAuthorized={isAuthorized}
      >
        <ButtonWithIcon
          text='Restore'
          disabled={!isAuthorized || (!vault && !sourceAccountAlsoRestore)}
          variant='outlined'
          onClick={onRestore}
          icon='material-symbols-settings-backup-restore-rounded'
        />
      </AuthorizationTooltip>
    </Stack>
  );
};
